import type { Language } from "@/lib/i18n";
import type { L5 } from "@/lib/localeText";

export const LANGUAGE_NAME_LABELS: Record<Language, L5> = {
  de: { de: "Deutsch", en: "German", it: "Tedesco", es: "Alemán", nl: "Duits" },
  en: { de: "Englisch", en: "English", it: "Inglese", es: "Inglés", nl: "Engels" },
  it: { de: "Italienisch", en: "Italian", it: "Italiano", es: "Italiano", nl: "Italiaans" },
  es: { de: "Spanisch", en: "Spanish", it: "Spagnolo", es: "Español", nl: "Spaans" },
  nl: { de: "Niederländisch", en: "Dutch", it: "Olandese", es: "Neerlandés", nl: "Nederlands" },
};

export const CATEGORY_LABELS: Record<string, L5> = {
  pendelleuchten: { de: "Pendelleuchten", en: "Pendant Lights", it: "Sospensioni", es: "Lámparas colgantes", nl: "Hanglampen" },
  deckenleuchten: { de: "Deckenleuchten", en: "Ceiling Lights", it: "Plafoniere", es: "Plafones", nl: "Plafondlampen" },
  wandleuchten: { de: "Wandleuchten", en: "Wall Lights", it: "Applique", es: "Apliques", nl: "Wandlampen" },
  stehleuchten: { de: "Stehleuchten", en: "Floor Lamps", it: "Lampade da terra", es: "Lámparas de pie", nl: "Vloerlampen" },
  tischleuchten: { de: "Tischleuchten", en: "Table Lamps", it: "Lampade da tavolo", es: "Lámparas de mesa", nl: "Tafellampen" },
  outdoor: { de: "Außenleuchten", en: "Outdoor Lights", it: "Illuminazione esterna", es: "Iluminación exterior", nl: "Buitenverlichting" },
  architektonisch: { de: "Architekturleuchten", en: "Architectural Lights", it: "Illuminazione architettonica", es: "Iluminación arquitectónica", nl: "Architecturale verlichting" },
  kronleuchter: { de: "Kronleuchter", en: "Chandeliers", it: "Lampadari", es: "Arañas", nl: "Kroonluchters" },
  einbauleuchten: { de: "Einbauleuchten", en: "Recessed Lights", it: "Faretti da incasso", es: "Empotrados", nl: "Inbouwverlichting" },
  skulptural: { de: "Skulpturale Leuchten", en: "Sculptural Lights", it: "Luci scultoree", es: "Luces escultóricas", nl: "Sculpturale verlichting" },
  customized: { de: "Customized", en: "Customized", it: "Personalizzati", es: "Personalizados", nl: "Op maat" },
  sofas: { de: "Sofas", en: "Sofas", it: "Divani", es: "Sofás", nl: "Sofa's" },
  sessel: { de: "Sessel", en: "Armchairs", it: "Poltrone", es: "Sillones", nl: "Fauteuils" },
  schlafsofas: { de: "Schlafsofas", en: "Sofa Beds", it: "Divani letto", es: "Sofás cama", nl: "Slaapbanken" },
  betten: { de: "Betten & Zubehör", en: "Beds & Accessories", it: "Letti e accessori", es: "Camas y accesorios", nl: "Bedden & accessoires" },
  "tische-stuehle": { de: "Tische & Stühle", en: "Tables & Chairs", it: "Tavoli e sedie", es: "Mesas y sillas", nl: "Tafels & stoelen" },
  accessoires: { de: "Accessoires", en: "Accessories", it: "Accessori", es: "Accesorios", nl: "Accessoires" },
  ergaenzungen: { de: "Ergänzungen", en: "Complements", it: "Complementi", es: "Complementos", nl: "Aanvullingen" },
  "regale-sideboards": { de: "Regale & Sideboards", en: "Shelving & Sideboards", it: "Scaffalature e sideboard", es: "Estanterías y aparadores", nl: "Stellingen & sideboards" },
  baenke: { de: "Bänke", en: "Benches", it: "Panche", es: "Bancos", nl: "Banken" },
  teppiche: { de: "Teppiche", en: "Rugs", it: "Tappeti", es: "Alfombras", nl: "Tapijten" },
};

export const MANUFACTURER_LINK_LABELS: Record<string, Record<string, L5>> = {
  vibieffe: {
    website: { de: "Zur Vibieffe Website", en: "Visit Vibieffe Website", it: "Visita il sito Vibieffe", es: "Visitar la web de Vibieffe", nl: "Bezoek de Vibieffe-website" },
    products: { de: "Vibieffe Produkte", en: "Vibieffe Products", it: "Prodotti Vibieffe", es: "Productos Vibieffe", nl: "Vibieffe-producten" },
    catalog: { de: "Vibieffe Kataloge", en: "Vibieffe Catalogues", it: "Cataloghi Vibieffe", es: "Catálogos de Vibieffe", nl: "Vibieffe-catalogi" },
  },
  "a-emotional-light": {
    website: { de: "Zur a·emotional light Website", en: "Visit a·emotional light Website", it: "Visita il sito di a·emotional light", es: "Visitar la web de a·emotional light", nl: "Bezoek de website van a·emotional light" },
    catalog: { de: "Kataloge herunterladen", en: "Download Catalogues", it: "Scarica i cataloghi", es: "Descargar catálogos", nl: "Catalogi downloaden" },
  },
  kdln: {
    website: { de: "Zur KDLN Website", en: "Visit KDLN Website", it: "Visita il sito KDLN", es: "Visitar la web de KDLN", nl: "Bezoek de KDLN-website" },
    products: { de: "Alle KDLN Produkte", en: "All KDLN Products", it: "Tutti i prodotti KDLN", es: "Todos los productos KDLN", nl: "Alle KDLN-producten" },
    catalog: { de: "KDLN auf Archiproducts", en: "KDLN on Archiproducts", it: "KDLN su Archiproducts", es: "KDLN en Archiproducts", nl: "KDLN op Archiproducts" },
  },
  "nemo-lighting": {
    website: { de: "Zur Nemo Website", en: "Visit Nemo Website", it: "Visita il sito Nemo", es: "Visitar la web de Nemo", nl: "Bezoek de Nemo-website" },
    products: { de: "Nemo Produkte", en: "Nemo Products", it: "Prodotti Nemo", es: "Productos Nemo", nl: "Nemo-producten" },
    external: { de: "Nemo Group", en: "Nemo Group", it: "Nemo Group", es: "Nemo Group", nl: "Nemo Group" },
  },
  "quasar-holland": {
    website: { de: "Zur Quasar Website", en: "Visit Quasar Website", it: "Visita il sito Quasar", es: "Visitar la web de Quasar", nl: "Bezoek de Quasar-website" },
    products_indoor: { de: "Innenbeleuchtung", en: "Indoor Lighting", it: "Illuminazione per interni", es: "Iluminación interior", nl: "Binnenverlichting" },
    products_chandeliers: { de: "Kronleuchter", en: "Chandeliers", it: "Lampadari", es: "Arañas", nl: "Kroonluchters" },
  },
  vistosi: {
    website: { de: "Zur Vistosi Website", en: "Visit Vistosi Website", it: "Visita il sito Vistosi", es: "Visitar la web de Vistosi", nl: "Bezoek de Vistosi-website" },
    products: { de: "Kollektionen entdecken", en: "Explore Collections", it: "Scopri le collezioni", es: "Explorar colecciones", nl: "Collecties ontdekken" },
  },
};

export function getLocalizedCategoryLabel(slug: string | null | undefined, language: Language, fallback?: string | null): string | null {
  if (!slug) return fallback ?? null;
  return CATEGORY_LABELS[slug]?.[language] ?? fallback ?? slug;
}

export function getLocalizedManufacturerLinkLabel(
  manufacturerSlug: string | null | undefined,
  linkType: string | null | undefined,
  rawLabel: string | null | undefined,
  language: Language,
): string {
  if (!rawLabel) return "";
  const slug = manufacturerSlug ?? "";
  const baseMap = MANUFACTURER_LINK_LABELS[slug];
  if (!baseMap) return rawLabel;

  const normalized = rawLabel.toLowerCase();
  const key =
    slug === "quasar-holland" && linkType === "products"
      ? normalized.includes("chandelier") || normalized.includes("kronleuchter")
        ? "products_chandeliers"
        : "products_indoor"
      : linkType ?? "";

  return baseMap[key]?.[language] ?? rawLabel;
}